import { useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Divider } from 'wix-ui-tpa/cssVars';
import { FormNestedSlot } from '../../../../../types/formSelectedSlots';
import {
  SelectedProduct,
  SlotService,
  V3Product,
} from '../../../../../utils/state/types';
import { TagName } from '../../../../../utils/tagName.const';
import Text, { TextType } from '../../../Text/Text';
import DateAndTime from '../../DateAndTime/DateAndTime';
import VideoConferenceBadge from '../../VideoConferenceBadge/VideoConferenceBadge';
import { ExpandableDetails } from '../SingleSlotDetails/ExpandableDetails';
import { SelectedRelatedProducts } from '../SingleSlotDetails/SelectedRelatedProducts';
import ParticipantDetails from '../SingleSlotDetails/ParticipantDetails';
import { MultiParticipantDetailsDataHook } from './dataHooks';
import { classes } from './MultiParticipantDetails.st.css';

export type MultiParticipantDetailsProps = {
  slotService: SlotService;
  slot: FormNestedSlot;
  selectedProductAddons?: SelectedProduct[];
  relatedProducts?: V3Product[];
  // Mobile: the whole Booking Details section already collapses, so no inner toggle.
  alwaysExpanded?: boolean;
};

/**
 * Booking Details when several participants are booked into one session:
 * "N participants" and the session time, then a block per participant with what each
 * one booked (behind a "More details" toggle unless alwaysExpanded).
 */
export const MultiParticipantDetails: React.FC<
  MultiParticipantDetailsProps
> = ({
  slotService,
  slot,
  selectedProductAddons,
  relatedProducts,
  alwaysExpanded = false,
}) => {
  const { t } = useTranslation();
  const { service, participants = [] } = slotService;
  const participantBlocks = participants.map((participant, index) => (
    <React.Fragment key={participant.id}>
      <ParticipantDetails
        slotService={slotService}
        slot={slot}
        participantIndex={index}
      />
      {/* The toggle below closes the list, so the last block needs no divider when
          the list is always expanded and nothing follows it. */}
      {(!alwaysExpanded || index < participants.length - 1) && (
        <Divider className={classes.divider} />
      )}
    </React.Fragment>
  ));

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Text
          type={TextType.Primary}
          tagName={TagName.Paragraph}
          data-hook={MultiParticipantDetailsDataHook.PARTICIPANTS_COUNT}
        >
          {t('app.bookings-form.bookings-details.participants-count', {
            count: participants.length,
          })}
        </Text>
        <DateAndTime
          className={classes.date}
          totalNumberOfSessions={service.totalNumberOfSessions}
          slot={slot}
          service={service}
        />
        <VideoConferenceBadge
          includesConferenceOption={service.includesConferenceOption}
        />
        <SelectedRelatedProducts
          selectedProductAddons={selectedProductAddons}
          relatedProducts={relatedProducts}
          paymentDetails={slotService.paymentDetails}
        />
      </div>
      <Divider className={classes.divider} />
      {alwaysExpanded ? (
        participantBlocks
      ) : (
        <ExpandableDetails className={classes.toggle}>
          {participantBlocks}
        </ExpandableDetails>
      )}
    </div>
  );
};

export default MultiParticipantDetails;
